import React, { useEffect, useMemo, useState } from "react";
import { Button } from "../components/ui/button";
import {
  Search,
  Upload,
  Download,
  Copy,
  MoreHorizontal,
  FileText,
  ArrowLeft,
  Plus,
  Eye,
} from "lucide-react";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
//import useNavigation from "../components/Context/NavigationContext";
import {
  useUpdateContentMutation,
  getContents,
  useContentsQuery,
  useDeleteContentMutation,
  useUploadJupyterFileMutation
} from "../api/jupyterManagementApi";
import { useJupyterWorkspace } from "../contexts/JupyterWorkspaceContext";
import paths from "../routes";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { useAuth } from "../App";

type NotebookType = "Project File" | "Template" | "User Created";

interface Notebook {
  id: string;
  name: string;
  description?: string;
  author?: string;
  lastModified?: string;
  size?: string;
  version?: string;
  type?: NotebookType;
  tags?: string[];
  // Carry path in practice, even if not declared in the interface
  // path?: string;
}

function getTypeColor(type?: NotebookType) {
  switch (type) {
    case "Project File":
      return "bg-bits-blue/10 text-bits-blue border-bits-blue";
    case "Template":
      return "bg-bits-gold/10 text-bits-gold border-bits-gold";
    case "User Created":
    default:
      return "bg-muted text-muted-foreground border-border";
  }
}

const generateUniqueFilename = (
  originalName: string,
  existingNames: Set<string>
): string => {
  if (!existingNames.has(originalName.toLowerCase())) {
    return originalName;
  }

  const lastDotIndex = originalName.lastIndexOf(".");
  const baseName =
    lastDotIndex !== -1
      ? originalName.substring(0, lastDotIndex)
      : originalName;
  const extension =
    lastDotIndex !== -1 ? originalName.substring(lastDotIndex) : "";
  const suffixMatch = baseName.match(/^(.*?)\((\d+)\)$/);
  let nameWithoutSuffix = suffixMatch ? suffixMatch[1] : baseName;
  let counter = suffixMatch ? parseInt(suffixMatch[2], 10) : 1;
  let candidate = `${nameWithoutSuffix}(${counter})${extension}`;
  while (existingNames.has(candidate.toLowerCase())) {
    counter++;
    candidate = `${nameWithoutSuffix}(${counter})${extension}`;
  }
  return candidate;
};

// Helpers to allow only .ipynb files
const getExt = (name?: string) => {
  if (!name) return "";
  const i = name.lastIndexOf(".");
  return i === -1 ? "" : name.slice(i + 1).toLowerCase();
};
const isIpynb = (name?: string) => getExt(name) === "ipynb";

export default function NotebooksPage() {
  const navigate = useNavigate();
  const { workspace } = useJupyterWorkspace();
  const { user } = useAuth();
  const updateContentMutation = useUpdateContentMutation();
  const deleteContentMutation = useDeleteContentMutation();
  const uploadLTIFileMutation = useUploadJupyterFileMutation();
  const queryClient = useQueryClient();
  const {
    data: contentsData,
    isLoading,
    error,
  } = useContentsQuery(workspace?.folderPath || "", workspace?.workspaceType === "local", !!workspace?.folderPath);
  const [searchTerm, setSearchTerm] = useState("");
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const { projectId } = useParams();

  const notebooks = useMemo(() => {
    if (!contentsData?.content) return [];
    const files = contentsData.content;
    return files
      ?.filter((f: any) => f.type === "notebook" || isIpynb(f?.name))
      .map((f: any) => ({
        id: f.path ?? f.name,
        name: f.name,
        description: f.description ?? "",
        author: f.author ?? "",
        lastModified: f.last_modified ?? f.lastModified ?? "",
        size: f.size ?? "",
        version: f.version ?? "",
        type: (f.type as NotebookType) ?? "User Created",
        tags: f.tags ?? [],
        path: f.path,
      }));
  }, [contentsData]);

  useEffect(() => {
    if (error) {
      console.error("Failed to fetch notebooks:", error);
      toast.error("Failed to load notebooks");
    }
  }, [error]);

  const filteredNotebooks = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    const base = notebooks?.filter((n) => isIpynb(n?.name));
    if (!q) return base;
    return base.filter(
      (n) =>
        n.name.toLowerCase().includes(q) ||
        (n.description ?? "").toLowerCase().includes(q) ||
        (n.tags ?? []).some((t) => t.toLowerCase().includes(q))
    );
  }, [notebooks, searchTerm]);

  const buildTargetPath = (filename: string) => {
    const basePath = workspace?.folderPath;
    return `${basePath}/${filename}`;
  };

 const MAX_FILE_SIZE = 50 * 1024 * 1024; // 50MB
const ALLOWED_EXTS = ["ipynb"];

const handleUpload = () => {
  const input = document.createElement("input");
  input.type = "file";
  input.multiple = true;
  input.accept = ".ipynb";
  input.onchange = async (e: any) => {
    const list = (e.target as HTMLInputElement).files;
    const selected = list ? Array.from(list) : [];
    if (!selected.length || !projectId) return;

    // cap per request
    if (selected.length > 20) {
      toast.error("You can upload at most 20 files at a time.");
      return;
    }

    // size check
    const tooLarge = selected.filter(f => f.size > MAX_FILE_SIZE);
    if (tooLarge.length) {
      toast.error(
        `Some files exceed the 50MB limit: ${tooLarge
          .map(f => `${f.name} (${(f.size / (1024 * 1024)).toFixed(1)}MB)`)
          .join(", ")}.`
      );
      return;
    }

    // type check
    const invalid = selected.filter(f => {
      const ext = f.name.split(".").pop()?.toLowerCase();
      return !ext || !ALLOWED_EXTS.includes(ext);
    });
    if (invalid.length) {
      toast.error(
        `Unsupported file types: ${invalid.map(f => f.name).join(", ")}.`
      );
      return;
    }

    // duplicates vs existing and within selection
    const existingNames = new Set(
      (notebooks ?? []).map(n => n.name.toLowerCase())
    );
    const dupExisting = selected.filter(f => existingNames.has(f.name.toLowerCase()));
    if (dupExisting.length) {
      toast.error(
        `Already exists: ${dupExisting.map(f => f.name).join(", ")}. Please rename.`
      );
      return;
    }
    const seen = new Map<string, number>();
    const dupInSelection = selected.filter(f => {
      const k = f.name.toLowerCase();
      seen.set(k, (seen.get(k) ?? 0) + 1);
      return (seen.get(k) ?? 0) > 1;
    });
    if (dupInSelection.length) {
      const names = [...new Set(dupInSelection.map(f => f.name))];
      toast.error(`Duplicate names in selection: ${names.join(", ")}.`);
      return;
    }

    const loading = toast.loading(`Uploading ${selected.length} files...`);
    try {
      await uploadLTIFileMutation.mutateAsync({ files: selected, projectId, username: user?.id ? `user_${user.id}` : undefined });
      toast.dismiss(loading);
      toast.success(`Uploaded ${selected.length} files successfully.`);
      await queryClient.invalidateQueries({ queryKey: ["jupyter", "contents"] });
      await queryClient.invalidateQueries({ queryKey: ["files", projectId] });
    } catch (error: any) {
      toast.dismiss(loading);
      if (error?.response?.status === 413) {
        toast.error("File too large. Please use a file smaller than 50MB.");
      } else if (error?.response?.status === 415) {
        toast.error("Unsupported file type. Please try a different format.");
      } else if (error?.response?.data?.data?.message) {
        toast.error(`Upload failed: ${error.response.data.data.message}`);
      } else {
        toast.error(`Upload failed: ${error?.response?.data?.message ?? "An unknown error occurred."}`);
      }
    }
  };
  input.click();
};

  const handleDownload = async (notebook: Notebook) => {
    try {
      const filePath = (notebook as any).path ?? buildTargetPath(notebook.name);
      const model = await getContents(filePath, workspace?.workspaceType === "local");

      const json = model?.content ? JSON.stringify(model.content, null, 2) : "";
      if (!json) {
        toast.error("No content available to download.");
        return;
      }

      const blob = new Blob([json], {
        // Many environments recognize application/x-ipynb+json; application/json is also acceptable
        type: "application/x-ipynb+json;charset=utf-8;",
      });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.setAttribute("href", url);
      link.setAttribute(
        "download",
        notebook.name.endsWith(".ipynb")
          ? notebook.name
          : `${notebook.name}.ipynb`
      );
      link.style.visibility = "hidden";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      toast.success(`Downloading ${notebook.name}`);
    } catch (err) {
      console.error("Download failed:", err);
      toast.error("Download failed");
    }
  };

  const handleDuplicate = async (notebook: Notebook) => {
    const loadingToast = toast.loading(`Duplicating ${notebook.name}...`);
    try {
      const resolvePath = (name: string) => {
        const basePath = workspace?.folderPath || "";
        return `${basePath}/${name}`;
      };
      const sourcePath = (notebook as any).path ?? resolvePath(notebook.name);

      const sourceModel = await getContents(sourcePath, workspace?.workspaceType === "local");
      const sourceJson = sourceModel?.content;
      if (!sourceJson) {
        toast.error("No content available to duplicate.");
        return;
      }
      // Generate unique -copy name
      const existingNames = new Set(notebooks?.map((n) => n.name.toLowerCase()));
      const parts = notebook.name.split(".");
      const ext = parts.length > 1 ? `.${parts.pop()}` : ".ipynb";
      const base = parts.join(".");
      const mkName = (idx: number | null) =>
        idx === null ? `${base}-copy${ext}` : `${base}-copy-${idx}${ext}`;

      let candidate = mkName(null);
      let idx = 1;
      while (existingNames.has(candidate.toLowerCase())) {
        candidate = mkName(idx++);
      }

      const targetPath = resolvePath(candidate);

      await updateContentMutation.mutateAsync({
        path: targetPath,
        content: sourceJson,
        type: "notebook",
        format: "json",
      });
      await queryClient.invalidateQueries({ queryKey: ["jupyter", "contents"] });
      await queryClient.invalidateQueries({ queryKey: ["files", projectId] });
      toast.success(`Duplicated ${notebook.name} → ${candidate}`);
    } catch (err: any) {
      console.error("Duplicate failed:", err);
      toast.error(err?.response?.data?.message || "Duplicate failed");
    } finally {
      toast.dismiss(loadingToast);
    }
  };

  const handleCreateNew = async () => {
    const loadingToast = toast.loading("Creating New ipynb file....");
    try {
      const basePath = workspace?.folderPath || "";
      if (!basePath) {
        toast.error("Create File Failed ! No Active Session");
        return;
      }
      const existingNames = new Set(notebooks?.map((n) => n.name.toLowerCase()));
      const base = "Untitled";
      const ext = ".ipynb";
      const mkName = (i: number) =>
        i === 0 ? `${base}${ext}` : `${base}-${i}${ext}`;
      let i = 0;
      let filename = mkName(i);
      while (existingNames.has(filename.toLowerCase())) {
        i += 1;
        filename = mkName(i);
      }
      const filePath = `${basePath}/${filename}`;
      const notebookJson = {
        nbformat: 4,
        nbformat_minor: 5,
        metadata: {
          kernelspec: {
            display_name: "Python 3",
            language: "python",
            name: "python3",
          },
          language_info: {
            name: "python",
          },
        },
        cells: [
          {
            cell_type: "code",
            execution_count: null,
            metadata: {},
            outputs: [],
            source: [""],
          },
        ],
      };

      const res: any = await updateContentMutation.mutateAsync({
        path: filePath,
        content: notebookJson,
        type: "notebook",
        format: "json",
      });
      await queryClient.invalidateQueries({ queryKey: ["jupyter", "contents"] });
      await queryClient.invalidateQueries({ queryKey: ["files", projectId] });
      toast.success(`Created ${filename}`);
    } catch (err: any) {
      console.error("Create new notebook failed:", err);
      toast.error(err?.response?.data?.message || "Failed to create notebook");
    } finally {
      toast.dismiss(loadingToast);
    }
  };

  const handleDelete = async (notebook: Notebook) => {
    if (deletingId) return;
    const isConfirmed = window.confirm(
      `Are you sure you want to delete "${notebook.name}"? This action cannot be undone.`
    );
    if (!isConfirmed) return;
    setDeletingId(notebook.id);
    try {
      const filePath = (notebook as any).path ?? buildTargetPath(notebook.name);
      await deleteContentMutation.mutateAsync(filePath);
      await queryClient.invalidateQueries({ queryKey: ["jupyter", "contents"] });
      await queryClient.invalidateQueries({ queryKey: ["files", projectId] });
      toast.success(`Deleted ${notebook.name}`);
    } catch (err: any) {
      console.error("Delete failed:", err);
      toast.error(
        err?.response?.data?.message ||
          err?.response?.data?.data?.message ||
          "Delete failed"
      );
    } finally {
      setDeletingId(null);
    }
  };

  const handleView = (notebook: Notebook) => {
    navigate(paths.notebook.replace(":projectId", projectId!), {
      state: { file: notebook },
    });
  };

  return (
    <div className="p-4 mt-4 space-y-2 min-h-screen bg-white">
      <div className="flex items-center">
        <Button
          variant="ghost"
          className="p-4 mt-2"
          onClick={() => {
            navigate(`${paths.notebook.replace(":projectId", projectId!)}`);
          }}
        >
          <ArrowLeft className="!h-6 !w-6 text-gray-700" />
        </Button>
        <h2 className="text-xl font-semibold flex gap-2 mt-2">Notebooks</h2>
        <div className="ml-auto flex items-center gap-2">
          <Button onClick={handleUpload} variant="secondary" className="mt-4">
            <Upload className="h-4 w-4" />
            Upload notebook
          </Button>
          <Button
            onClick={handleCreateNew}
            variant="secondary"
            className="mt-4"
          >
            <Plus className="h-4 w-4" />
            New notebook
          </Button>
        </div>
      </div>

      <p className="text-muted-foreground p-2">
        Manage Jupyter notebooks, upload new files, or create new notebooks for
        assignments
      </p>

      <div className="relative">
        <Search className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Search notebooks..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-9"
        />
      </div>

      <div className="grid gap-3 mt-2">
        {filteredNotebooks?.map((n) => (
          <div key={n.id} className="border rounded-lg p-3 bg-background">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                {/* <span className={`border px-2 py-0.5 rounded text-xs ${getTypeColor(n.type)}`}>
                  {n.type ?? "User Created"}
                </span> */}
                <span className="font-medium">{n.name}</span>
                {/* <span className="text-muted-foreground">· {n.version ?? "v1"}</span> */}
              </div>
              <div className="flex items-center gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => handleView(n)}
                >
                  <Eye className="h-4 w-4" />
                  Open
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => handleDownload(n)}
                >
                  Download
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => handleDuplicate(n)}
                >
                  Duplicate
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => handleDelete(n)}
                  disabled={deletingId !== null}
                >
                  {deletingId === n.id ? (
                    <div className="h-4 w-4 animate-spin rounded-full border-2 border-gray-300 border-t-white" />
                  ) : (
                    <>Delete</>
                  )}
                </Button>
              </div>
            </div>

            <div className="mt-1 text-sm text-muted-foreground">
              {n.description ?? ""}
            </div>

            <div className="mt-2 flex items-center gap-3 text-xs text-muted-foreground">
              {/* <span>👤 {n.author ?? "—"}</span>
              <span>⏰ {n.lastModified ?? "—"}</span> */}
              <span>📁 {n.size ?? "—"}kb</span>
            </div>

            <div className="mt-2 flex gap-2">
              {(n.tags ?? [])?.map((tag) => (
                <Badge key={tag} variant="outline">
                  {tag}
                </Badge>
              ))}
            </div>
          </div>
        ))}

        {isLoading ? (
          <div className="flex flex-col items-center justify-center min-h-[400px] text-center">
            <div className="h-12 w-12 animate-spin rounded-full border-4 border-gray-200 border-t-bits-blue mb-4" />
            <p className="text-lg font-medium text-gray-900">
              Loading notebooks...
            </p>
          </div>
        ) : filteredNotebooks.length === 0 ? (
          <div className="flex flex-col items-center justify-center min-h-[400px] text-center">
            {searchTerm.trim() !== "" ? (
              <div className="space-y-2">
                <FileText className="h-12 w-12 text-muted-foreground mx-auto" />
                <p className="text-lg font-medium text-gray-900">
                  No notebooks match your search
                </p>
                <p className="text-sm text-muted-foreground">
                  Try adjusting your search term or create a new notebook
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                <FileText className="h-16 w-16 text-muted-foreground mx-auto" />
                <div className="space-y-2">
                  <p className="text-lg font-medium text-gray-900">
                    No notebooks available
                  </p>
                  <p className="text-sm text-muted-foreground">
                    Create a new notebook or upload an existing one to get
                    started
                  </p>
                </div>
              </div>
            )}
          </div>
        ) : null}
      </div>
    </div>
  );
}
